import { Link } from "react-router-dom";
import { useState, useEffect } from "react";

interface PrimaryButtonProps {
  to?: string;
  href?: string;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  periodic?: boolean;
}

export const PrimaryButton = ({
  to,
  href,
  onClick,
  children,
  className = "",
  periodic = false,
}: PrimaryButtonProps) => {
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (!periodic) return;

    const interval = setInterval(() => {
      setAnimate(true);
      setTimeout(() => setAnimate(false), 2000);
    }, 30000);

    return () => clearInterval(interval);
  }, [periodic]);

  const baseClassName = `
    inline-flex items-center justify-center
    rounded-full
    border border-ergo-500/30 dark:border-ergo-400/30
    bg-ergo-50/60 dark:bg-ergo-900/30
    px-6 py-2.5 text-sm font-medium
    text-ergo-700 dark:text-ergo-300
    hover:bg-ergo-100 dark:hover:bg-ergo-900/50
    hover:border-ergo-500/50 dark:hover:border-ergo-400/50
    transition-all duration-200
    active:scale-[0.98]
    ${animate ? "animate-gentle-pulse" : ""}
  `;

  // External link
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        className={`${baseClassName} ${className}`}
      >
        {children}
      </a>
    );
  }

  // Internal route
  if (to) {
    return (
      <Link to={to} onClick={onClick} className={`${baseClassName} ${className}`}>
        {children}
      </Link>
    );
  }

  // Regular button
  return (
    <button onClick={onClick} className={`${baseClassName} ${className}`}>
      {children}
    </button>
  );
};
